<!-- Another leg on the ticket — or a whole multi of them — from the board the
     app already holds.

     THE ROW IS A MATCH, NOT A SELECTION, because that is how a punter reads a
     betting screen: you find the game, then you pick a side. A list of every
     side on the board is twice as long, says the fixture twice, and makes
     somebody scan for the second half of a match they have already found.

     WHAT "BEST" MEANS HERE IS ONE THING ONLY: the price at this ticket's book
     against what the other books think, with their margin taken off
     (`rankCandidates` in lib/engine/reprice.js). It is not a tip and the note
     under the segment says so — there is no model in it, no form, no opinion
     about who wins. A side fewer than three books have priced is not ranked at
     all: it sits at the foot reading "few books", because an edge resting on
     one other book is a disagreement, not a finding.

     Several matches can be selected in one visit. Building a combo is the
     point, and three separate visits would put three tickets through the paper
     trail for one act — see `addLegs` in the store.

     TWO MODES, ONE SHEET. `add` puts legs on a ticket that exists; `build`
     starts from nothing and prints the punter's own multi. They are the same
     screen because they are the same question — which of today's prices is
     worth taking — and the only differences are what it is called, what the
     empty slip says, and whether the button prints or adds.

     Nothing here fetches. The board in the picker is the board on the phone. -->
<template>
  <IonModal :is-open="isOpen" @didDismiss="emit('close')">
    <IonHeader>
      <IonToolbar>
        <IonTitle>{{ title }}</IonTitle>
        <IonButtons slot="end">
          <IonButton @click="emit('close')">Cancel</IonButton>
        </IonButtons>
      </IonToolbar>
      <IonToolbar>
        <IonSearchbar
          v-model="query"
          class="search"
          placeholder="Team, match, venue or runner"
          aria-label="Search the board by team, match, venue or runner"
          :debounce="120"
          inputmode="search"
        />
      </IonToolbar>
      <!-- One chip per sport, with what each still has to offer. Scrollable:
           a Saturday carries six boards and they are not going to fit across a
           phone. The same segment the Matches tab filters with. -->
      <IonToolbar v-if="chips.length > 1">
        <IonSegment v-model="sport" class="sport-chips" scrollable>
          <IonSegmentButton value="all"><IonLabel>All</IonLabel></IonSegmentButton>
          <IonSegmentButton v-for="chip in chips" :key="chip.sport" :value="chip.sport">
            <IonLabel>{{ chip.label }} · {{ chip.count }}</IonLabel>
          </IonSegmentButton>
        </IonSegment>
      </IonToolbar>
    </IonHeader>

    <IonContent>
      <div class="pad-page">
        <!-- THE SORT AND THE ⓘ SHARE A ROW, because the question the owner
             actually asked was about both at once: "what's best value vs
             kick-off, and what's the −3.6% mean?" The two sentences under it
             answer them in that order; the ⓘ is for the reader who wants the
             two edge cases as well. -->
        <div class="sort">
          <IonSegment v-model="sort" class="sort-segment">
            <IonSegmentButton value="value"><IonLabel>Best value</IonLabel></IonSegmentButton>
            <IonSegmentButton value="kickoff"><IonLabel>Kick-off</IonLabel></IonSegmentButton>
          </IonSegment>
          <!-- Ionic strips `aria-label` off the host, so the name is carried
               by the screen-reader-only span — the KickoffGroup pattern. -->
          <IonButton
            class="info-btn"
            fill="clear"
            size="small"
            aria-label="How this list works"
            @click="helpOpen = true"
          >
            <IonIcon slot="icon-only" :icon="informationCircleOutline" aria-hidden="true" />
            <span class="sr-only">How this list works</span>
          </IonButton>
        </div>
        <p class="small-print note">{{ SORT_NOTE }}</p>
        <p class="small-print note">{{ EDGE_NOTE }}</p>

        <!-- WHAT IS ALREADY ON IT, first and unselectable. A picker that simply
             left them out would have somebody hunting for a match they put on
             themselves two taps ago. -->
        <ul v-if="onTicket.length" class="on-ticket">
          <li v-for="leg in onTicket" :key="leg.matchKey" class="on-row">
            <span class="on-fixture">{{ leg.match || leg.selection }}</span>
            <span class="on-mark">On the ticket</span>
          </li>
        </ul>

        <p v-if="!rows.length" class="empty">{{ emptyLine }}</p>

        <ul v-else class="matches">
          <li v-for="row in rows" :key="row.matchKey" class="match">
            <p class="eyebrow">{{ eyebrow(row) }}</p>
            <p class="fixture">{{ row.match }}</p>
            <div class="sides">
              <button
                v-for="side in row.sides"
                :key="side.side"
                type="button"
                class="side"
                :class="{ 'side--on': isOn(side) }"
                :aria-pressed="isOn(side)"
                :aria-label="labelFor(side)"
                @click="toggle(side)"
              >
                <span class="side-name">{{ shortName(side) }}</span>
                <span class="side-price print">{{ price(side.price) }}</span>
                <span class="side-edge" :class="{ 'side-edge--go': (side.edge ?? -1) >= 0 }">{{ edgeText(side) }}</span>
                <span v-if="side.bookmakerKey !== bookKey" class="side-book">best price</span>
              </button>
            </div>
          </li>
        </ul>

        <p v-if="underWay" class="small-print under-way">{{ underWay }} under way · hidden</p>
      </div>
    </IonContent>

    <!-- THE RUNNING SLIP. Every tap above changes what the ticket pays, and a
         punter picking a fourth leg is asking exactly that question — so the
         answer is on screen before they commit, not in a toast afterwards. -->
    <IonFooter>
      <IonToolbar>
        <div class="slip">
          <p class="slip-line print">{{ slipLine }}</p>
          <IonButton class="add" :disabled="!picks.length || save.busy.value" @click="submit">
            {{ save.busy.value ? (building ? 'Printing…' : 'Adding…') : addLabel }}
          </IonButton>
        </div>
        <p v-if="save.error.value" class="field-error slip-error">{{ save.error.value }}</p>
      </IonToolbar>
    </IonFooter>
  </IonModal>

  <!-- "How this list works" — the two sentences under the segment, plus the
       two words on the rows that are not self-explanatory. Its own modal over
       this one rather than a page the punter has to leave the picker for: they
       are mid-build, and a question about a number is not a reason to lose the
       legs already chosen. -->
  <IonModal :is-open="helpOpen" @didDismiss="helpOpen = false">
    <IonHeader>
      <IonToolbar>
        <IonTitle>How this list works</IonTitle>
        <IonButtons slot="end">
          <IonButton @click="helpOpen = false">Done</IonButton>
        </IonButtons>
      </IonToolbar>
    </IonHeader>
    <IonContent>
      <div class="pad-page how-it-works">
        <section>
          <h2>Best value, or kick-off</h2>
          <p>{{ SORT_NOTE }}</p>
        </section>

        <section>
          <h2>The percentage</h2>
          <p>{{ EDGE_NOTE }}</p>
        </section>

        <section>
          <h2>“few books”</h2>
          <p>{{ THIN_NOTE }}</p>
        </section>

        <section>
          <h2>“best price”</h2>
          <p>{{ BEST_PRICE_NOTE }}</p>
        </section>
      </div>
    </IonContent>
  </IonModal>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import {
  IonModal, IonHeader, IonFooter, IonToolbar, IonTitle, IonButtons, IonButton,
  IonContent, IonIcon, IonSearchbar, IonSegment, IonSegmentButton, IonLabel,
} from '@ionic/vue';
import { informationCircleOutline } from 'ionicons/icons';
import { useAction } from '../core/ui';
import { prefs } from '../core/storage.js';
import { money, price, signedPct } from '../lib/format.js';
// Pure, and the same two functions the picker is tested through.
import { filterCandidates, matchRows } from '../lib/engine/reprice.js';
import { kickoffLabel } from '../lib/timeline.js';

const props = defineProps({
  isOpen: { type: Boolean, default: false },
  /** Every side that could go on, as `rankCandidates` returns them. */
  candidates: { type: Array, default: () => [] },
  /** The ticket's own legs, for the "On the ticket" rows: `{ matchKey, match, selection }`. */
  onTicket: { type: Array, default: () => [] },
  /** How many matches are hidden because they have started. */
  underWay: { type: Number, default: 0 },
  /** The book this ticket's prices come from, so a fallback price can say so. */
  bookKey: { type: String, default: null },
  /** Is there a board at all? Changes which empty state is true. */
  hasBoard: { type: Boolean, default: true },
  /** (picks) => { legs, odds, returns } | null — the page's own `withLegs` preview. */
  preview: { type: Function, required: true },
  /** (picks) => Promise — the page writes the edit, toasts and closes. */
  save: { type: Function, required: true },
  /**
   * `add` — legs onto a ticket that exists. `build` — the punter's own multi
   * from nothing, which is the same picker with an empty slip and a print
   * button. Nothing else about the screen changes.
   */
  mode: { type: String, default: 'add' },
  /** The clock, for the kick-off labels. */
  now: { type: Number, default: () => Date.now() },
});

const emit = defineEmits(['close']);

/**
 * WHAT THE NUMBERS MEAN, in the punter's words rather than the engine's.
 *
 * The owner read a screen of "−3.6%, −6.8%" and asked two questions: what is
 * the difference between Best value and Kick-off, and what is the minus sign.
 * The old note said "Edge is the book's price against the other books'
 * average", which answers neither — it names the quantity and explains
 * nothing, and it never said that MOST legs are negative because that is what
 * a bookmaker's margin looks like from the outside.
 *
 * ONE COPY, TWO PLACES: the same strings print under the segment and in the ⓘ,
 * so the short answer and the long one cannot come to disagree.
 */
const SORT_NOTE = 'Best value lists the runners and sides your book is paying most over the fair price; Kick-off lists them in start order.';
const EDGE_NOTE = 'The % is your book’s price against the fair price from the other books (and the exchange): + means it pays over fair, − means under. Most legs are −: that’s the bookmaker’s margin. Not a tip.';
const THIN_NOTE = 'Fewer than three books have priced that one, so there is no market to call one of them wrong. Those rows are never ranked: they sit at the foot, with no percentage.';
const BEST_PRICE_NOTE = 'Your own book is not pricing that match or race, so the row is the longest price on the board instead, at whichever book is paying it. The leg goes on either way.';

/** The ⓘ, which is a modal over this one — see the note on it. */
const helpOpen = ref(false);

const building = computed(() => props.mode === 'build');
const title = computed(() => (building.value ? 'Build a multi' : 'Add a leg'));

const query = ref('');
const sport = ref('all');
/**
 * Best value or kick-off, REMEMBERED. Unlike the Matches tab's sport chip —
 * which is a way of reading today's board and must not outlive it — this is a
 * statement about how somebody likes to shop, and re-choosing it every time
 * would be the app forgetting something it was told.
 */
const sort = ref(prefs.legPickerSort === 'kickoff' ? 'kickoff' : 'value');
watch(sort, (v) => { prefs.legPickerSort = v; });

/** Chosen sides, `{ matchKey, side }`, at most one per match. */
const picks = ref([]);

// Every visit starts on the board, not on the last one's leftovers.
watch(() => props.isOpen, (open) => {
  if (!open) return;
  picks.value = [];
  query.value = '';
  sport.value = 'all';
});

const grouped = computed(() => matchRows(props.candidates, { sort: sort.value }));

/**
 * One chip per sport, busiest first — counted off what is actually addable.
 *
 * `sportTitle` rather than the competition, which is the same string for a
 * fixture ("AFL") and the MEETING for a race ("Randwick 2026-09-15"): the three
 * racing chips are Horses, Greyhounds and Harness, not one chip per venue. They
 * appear only when the card has races to offer, which falls straight out of the
 * rows — no card, no racing rows, no chips.
 */
const chips = computed(() => {
  const by = new Map();
  for (const row of grouped.value) {
    const label = row.sportTitle || row.competition || row.sport;
    const found = by.get(row.sport) ?? { sport: row.sport, label, count: 0 };
    found.count += 1;
    by.set(row.sport, found);
  }
  return [...by.values()].sort((a, b) => b.count - a.count || String(a.label).localeCompare(String(b.label)));
});

const rows = computed(() => filterCandidates(grouped.value, {
  query: query.value,
  sport: sport.value === 'all' ? null : sport.value,
}));

const emptyLine = computed(() => {
  if (!props.hasBoard) return 'No board yet · pull to refresh on Today.';
  if (query.value.trim() || sport.value !== 'all') return 'Nothing on the board matches that.';
  return 'Every match on today’s board is already on this ticket.';
});

const eyebrow = (row) => [row.sportTitle || row.competition || row.sport, kickoffLabel(Date.parse(row.kickoff), props.now)]
  .filter(Boolean).join(' · ');

/** "Collingwood to win" is the leg's own wording; the button has room for a name. */
const shortName = (side) => String(side.selection ?? '').replace(/ to win$/, '');

const edgeText = (side) => (side.thin || !Number.isFinite(side.edge) ? 'few books' : `${signedPct(side.edge)}`);

const labelFor = (side) => {
  const worth = side.thin || !Number.isFinite(side.edge)
    ? 'few books pricing it'
    : `${signedPct(side.edge)} vs fair`;
  return `Add ${shortName(side)} at ${price(side.price)}, ${worth}`;
};

const isOn = (side) => picks.value.some((p) => p.matchKey === side.matchKey && p.side === side.side);

/**
 * One side per match: tapping the other one MOVES the choice rather than
 * refusing it. Backing both sides of a game in a multi is the one thing a multi
 * cannot do, and a picker that made somebody undo before they could change
 * their mind would be pedantry.
 */
function toggle(side) {
  if (isOn(side)) {
    picks.value = picks.value.filter((p) => !(p.matchKey === side.matchKey && p.side === side.side));
    return;
  }
  picks.value = [
    ...picks.value.filter((p) => p.matchKey !== side.matchKey),
    { matchKey: side.matchKey, side: side.side },
  ];
}

const shown = computed(() => (picks.value.length ? props.preview(picks.value) : null));

/**
 * The count is the label, because the count is the thing a punter is keeping
 * in their head while they tap. "Add 0 legs" is not a sentence anybody says,
 * so the empty state is the bare verb — disabled, and waiting.
 *
 * A build PRINTS, because that is what comes out: a ticket. And one leg is a
 * single rather than "1 leg" — a punter who picks one side has built a single,
 * which the app is happy to print and says so in its own words.
 */
const addLabel = computed(() => {
  const n = picks.value.length;
  if (building.value) {
    if (!n) return 'Print';
    return n === 1 ? 'Print a single' : `Print ${n} legs`;
  }
  if (!n) return 'Add';
  return n === 1 ? 'Add 1 leg' : `Add ${n} legs`;
});

const slipLine = computed(() => {
  const s = shown.value;
  if (!s) return building.value ? 'Pick your legs' : 'Pick a side to see what it pays.';
  return `${s.legs} leg${s.legs === 1 ? '' : 's'} · ${money(s.odds)} · Returns ${money(s.returns)}`;
});

const save = useAction((list) => props.save(list), { done: '', failed: 'Couldn’t add that leg. Try again.' });

const submit = () => save.run(picks.value);
</script>

<style scoped>
/* The search row and the chips are the bar's, so they hold at the top while the
   board scrolls under them — a filter that scrolls away is a filter somebody
   has to go back for. */
.search {
  --background: transparent;
  --box-shadow: none;
  padding: 0;
}

.sport-chips {
  margin: 0;
}

/* Sticky under the bar, because the order matters more the further down the
   list you are: it is the one control that changes what "first" means. The ⓘ
   rides beside it at the tap size the rest of the app holds. */
.sort {
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  padding: var(--sp-2) 0;
  background: var(--pad);
}

.sort-segment {
  flex: 1 1 auto;
  min-width: 0;
}

.info-btn {
  flex: 0 0 auto;
  --padding-start: 0;
  --padding-end: 0;
  min-width: var(--tap);
  min-height: var(--tap);
  margin: 0;
  color: var(--carbon-faint);
}

.note {
  margin: 0 0 var(--sp-2);
}

.note:last-of-type {
  margin-bottom: var(--sp-3);
}

/* Copied from PicksHelp's `.how-it-works`, not imported — reaching into
   another file's scoped styles breaks the moment that file changes. */
.how-it-works section {
  padding-bottom: var(--sp-4);
  border-bottom: 1px solid var(--rule);
  margin-bottom: var(--sp-4);
}

.how-it-works section:last-child {
  border-bottom: 0;
}

.how-it-works h2 {
  margin: 0 0 var(--sp-3);
  font: 700 var(--fs-body)/1.25 var(--font-body);
  color: var(--carbon);
}

.how-it-works p {
  margin: 0;
  font: 500 var(--fs-body)/1.5 var(--font-body);
  color: var(--carbon);
}

.on-ticket,
.matches {
  margin: 0;
  padding: 0;
  list-style: none;
}

/* What is already on the ticket: dimmed, unselectable, and above the rest so
   the reader can see their own ticket in the list they are shopping from. */
.on-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-2);
  padding: var(--sp-2) 0;
  border-top: 1px solid var(--rule);
  opacity: 0.55;
}

.on-fixture {
  font: 500 var(--fs-meta)/1.3 var(--font-body);
  color: var(--carbon);
}

.on-mark {
  flex: 0 0 auto;
  font: 700 var(--fs-small)/1.3 var(--font-body);
  color: var(--carbon-faint);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

/* One fixture, one block: when and what above, the prices under it. The
   hairline between is the board's own idiom — the ticket's legs are ruled the
   same way. */
.match {
  padding: var(--sp-3) 0;
  border-top: 1px solid var(--rule);
}

.eyebrow {
  margin: 0;
  font: 700 var(--fs-small)/1.3 var(--font-body);
  color: var(--carbon-faint);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.fixture {
  margin: 2px 0 var(--sp-2);
  font: 700 var(--fs-body)/1.25 var(--font-body);
  color: var(--carbon);
}

/* TWO SIDES ACROSS, OR TWELVE RUNNERS WRAPPED. A fixture has two buttons and
   they share the row as they always have; a race has as many as the field, and
   a flex row of twelve would squeeze each to a sliver and then push the page
   sideways at 320. Wrapping is the whole fix: `1 1 96px` keeps three across a
   narrow phone and lets two fill the row when two is all there is. */
.sides {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-2);
}

/* A price button, sized like one: the team, the price, and what it is worth,
   stacked so three of them still fit across 320. Selected is FILLED rather
   than ticked — the state has to be readable at a glance with a thumb over
   half the row. */
.side {
  flex: 1 1 96px;
  min-width: 0;
  min-height: var(--tap);
  padding: var(--sp-2);
  border: 1.5px solid var(--rule);
  border-radius: var(--r-control);
  background: transparent;
  color: var(--carbon);
  text-align: left;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.side--on {
  border-color: var(--carbon);
  background: var(--carbon);
  color: var(--pad);
}

.side:focus-visible {
  outline: 2px solid var(--carbon);
  outline-offset: 2px;
}

.side-name,
.side-price,
.side-edge,
.side-book {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.side-name {
  font: 500 var(--fs-meta)/1.25 var(--font-body);
}

.side-price {
  font: 700 var(--fs-head)/1.2 var(--font-body);
}

.side-edge {
  font: 700 var(--fs-small)/1.3 var(--font-body);
  color: var(--carbon-faint);
}

/* The one place green is spent: a price over the rest of the market. Inside a
   selected button the ink is the paper, or the chip would vanish into it. */
.side-edge--go {
  color: var(--go);
}

.side--on .side-edge,
.side--on .side-book {
  color: var(--pad);
}

.side-book {
  font: 500 var(--fs-small)/1.3 var(--font-body);
  color: var(--carbon-faint);
}

.empty,
.under-way {
  margin: var(--sp-4) 0 0;
  font: 500 var(--fs-meta)/1.45 var(--font-body);
  color: var(--carbon-faint);
}

/* The slip: what the ticket would pay, and the one button that commits it. */
.slip {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  padding: var(--sp-2) var(--sp-3);
}

.slip-line {
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  font: 700 var(--fs-meta)/1.3 var(--font-body);
  color: var(--carbon);
}

.add {
  flex: 0 0 auto;
  margin: 0;
  min-height: var(--tap);
}

.slip-error {
  margin: 0 var(--sp-3) var(--sp-2);
}
</style>
